import React, { useEffect, useState, useRef } from "react";
import { subConfig, subMe, subSubmit, subCouponCheck, subRedeem } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, ShieldCheck, Copy, Clock, AlertCircle, CheckCircle2, XCircle, Tag, Sparkles } from "lucide-react";

export default function Paywall({ onUnlocked }) {
  const [cfg, setCfg] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [previewB64, setPreviewB64] = useState(null);
  const [txHash, setTxHash] = useState("");
  const [note, setNote] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);  // {valid, percent_off, discounted_amount, code, label}
  const [couponChecking, setCouponChecking] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);  // collapsed by default — codes are private
  const fileRef = useRef(null);

  useEffect(() => {
    Promise.all([subConfig(), subMe()]).then(([c, m]) => {
      setCfg(c); setMe(m); setLoading(false);
      if (m.status === "active") onUnlocked?.();
    }).catch(() => setLoading(false));
    const iv = setInterval(() => {
      subMe().then(m => {
        setMe(m);
        if (m.status === "active") onUnlocked?.();
      }).catch(() => {});
    }, 30000);
    return () => clearInterval(iv);
  }, [onUnlocked]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("Image must be < 8 MB");
    const reader = new FileReader();
    reader.onload = (ev) => setPreviewB64(ev.target.result);
    reader.readAsDataURL(f);
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return toast.error("Enter a coupon code");
    setCouponChecking(true);
    try {
      const info = await subCouponCheck(couponCode.trim());
      setCouponInfo(info);
      const dur = info.is_permanent ? "permanent" : `${info.duration_days}-day`;
      toast.success(`${info.code} accepted — ${dur} free access ready to unlock.`);
    } catch (e) {
      setCouponInfo(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    } finally { setCouponChecking(false); }
  };

  const removeCoupon = () => { setCouponCode(""); setCouponInfo(null); };

  const redeemFree = async () => {
    if (!couponInfo?.valid) return;
    setSubmitting(true);
    try {
      const res = await subRedeem(couponInfo.code);
      toast.success(res.message || "Unlocked!");
      const m = await subMe();
      setMe(m);
      if (m.status === "active") onUnlocked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Redeem failed");
    } finally { setSubmitting(false); }
  };

  const submit = async () => {
    if (!previewB64) return toast.error("Please upload your payment screenshot first");
    setSubmitting(true);
    try {
      await subSubmit({
        tx_screenshot_b64: previewB64,
        tx_hash: txHash || null,
        note: note || null,
        coupon_code: couponInfo?.code || null,
      });
      toast.success("Submitted — verification within 2 hours.");
      const m = await subMe();
      setMe(m);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const copyAddr = () => {
    if (cfg?.address) {
      navigator.clipboard.writeText(cfg.address);
      toast.success("Address copied");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-black"><Loader2 className="w-8 h-8 animate-spin text-zinc-500" /></div>;

  const status = me?.status || "none";
  const hasCoupon = !!couponInfo?.valid;

  return (
    <div className="min-h-screen bg-black text-white px-4 py-8 md:py-16">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <ShieldCheck className="w-10 h-10 text-[#00E676] mx-auto mb-3" />
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Signal Academy Hub</h1>
          <p className="text-zinc-400 mt-2 text-sm">Access requires an active subscription — manual USDT verification.</p>
        </div>

        {/* Status banner */}
        {status === "pending" && (
          <div data-testid="sub-status-pending" className="border border-[#FFCC00]/40 bg-[#FFCC00]/5 rounded-md p-4 mb-6 flex items-start gap-3">
            <Clock className="w-5 h-5 text-[#FFCC00] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-mono text-sm text-[#FFCC00]">Payment under review</div>
              <p className="text-xs text-zinc-300 mt-1">Submitted {me.submitted_at ? new Date(me.submitted_at).toLocaleString() : ""}. Our team verifies within 2 hours. This page auto-refreshes every 30 seconds.</p>
            </div>
          </div>
        )}
        {status === "rejected" && (
          <div data-testid="sub-status-rejected" className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 rounded-md p-4 mb-6 flex items-start gap-3">
            <XCircle className="w-5 h-5 text-[#FF3B30] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-mono text-sm text-[#FF3B30]">Submission rejected</div>
              <p className="text-xs text-zinc-300 mt-1">{me.admin_note || "Please re-submit with a clearer screenshot of your USDT TRC20 transfer."}</p>
            </div>
          </div>
        )}
        {status === "expired" && (
          <div data-testid="sub-status-expired" className="border border-[#FFCC00]/40 bg-[#FFCC00]/5 rounded-md p-4 mb-6">
            <div className="font-mono text-sm text-[#FFCC00]">Subscription expired — renew below to continue.</div>
          </div>
        )}

        {/* Payment instructions */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="border border-zinc-900 bg-[#0F0F0F] rounded-md p-5">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1">Amount</div>
            <div className="font-mono text-3xl text-[#00E676] font-bold" data-testid="sub-effective-amount">
              {hasCoupon ? (
                <span>
                  <span className="line-through text-zinc-600 text-lg mr-2">${cfg?.amount_usd}</span>
                  FREE
                </span>
              ) : (
                <span>${cfg?.amount_usd} USD</span>
              )}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              {hasCoupon
                ? <span className="text-[#00E676]">Coupon <span className="font-mono">{couponInfo.code}</span> · {couponInfo.is_permanent ? "Permanent access" : `${couponInfo.duration_days}-day access`} · {couponInfo.label}</span>
                : <>in USDT on {cfg?.network} network · 30 days access</>}
            </div>
            <div className="mt-5 text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1">Address</div>
            <div className="flex items-start gap-2">
              <code data-testid="sub-address" className="text-xs text-white font-mono break-all bg-black border border-zinc-800 rounded-sm px-2 py-2 flex-1">{cfg?.address}</code>
              <button data-testid="sub-copy-address" onClick={copyAddr} className="px-2 py-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 rounded-sm flex-shrink-0">
                <Copy className="w-4 h-4 text-zinc-400" />
              </button>
            </div>
            <ul className="mt-4 space-y-1.5">
              {cfg?.instructions?.map((line, i) => (
                <li key={i} className="text-xs text-zinc-400 flex gap-2"><span className="text-[#00E676] mt-0.5">•</span><span>{line}</span></li>
              ))}
            </ul>
          </div>
          <div className="border border-zinc-900 bg-[#0F0F0F] rounded-md p-5 flex flex-col items-center justify-center">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">Scan to pay</div>
            {cfg?.qr_image_url ? (
              <img data-testid="sub-qr" src={cfg.qr_image_url} alt="USDT TRC20 QR" className="w-56 h-56 object-contain bg-black border border-zinc-800 rounded-sm" />
            ) : (
              <div className="w-56 h-56 bg-black border border-zinc-800 rounded-sm flex items-center justify-center text-zinc-600 text-xs">QR unavailable</div>
            )}
            <p className="text-[10px] text-zinc-500 mt-2 font-mono">No memo required</p>
          </div>
        </div>

        {/* Coupon block — collapsed by default, private codes only */}
        {status !== "pending" && status !== "active" && (
          <div className="mb-6">
            {!couponOpen && !couponInfo?.valid ? (
              <button
                data-testid="sub-coupon-toggle"
                onClick={() => setCouponOpen(true)}
                className="text-xs text-zinc-500 hover:text-zinc-300 font-mono underline-offset-2 hover:underline transition-colors"
              >
                Have a private access code?
              </button>
            ) : (
              <div className="border border-zinc-900 bg-[#0F0F0F] rounded-md p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-[#FFCC00]" />
                    <h2 className="font-display text-sm font-semibold uppercase tracking-wider">Private Access Code</h2>
                  </div>
                  {!couponInfo?.valid && (
                    <button
                      data-testid="sub-coupon-close"
                      onClick={() => { setCouponOpen(false); setCouponCode(""); }}
                      className="text-zinc-500 hover:text-white text-xs"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    data-testid="sub-coupon-input"
                    value={couponCode}
                    onChange={e => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="ENTER CODE"
                    disabled={!!couponInfo?.valid}
                    autoFocus
                    className="flex-1 bg-black border border-zinc-800 text-white px-3 py-2.5 rounded-sm font-mono text-sm uppercase tracking-wider focus:border-[#FFCC00] outline-none disabled:opacity-60"
                  />
                  {!couponInfo?.valid ? (
                    <button
                      data-testid="sub-coupon-apply-btn"
                      onClick={applyCoupon}
                      disabled={couponChecking || !couponCode.trim()}
                      className="px-5 py-2.5 bg-[#FFCC00] hover:bg-[#FFD633] text-black font-semibold rounded-sm text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                      {couponChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
                      Apply
                    </button>
                  ) : (
                    <button
                      data-testid="sub-coupon-remove-btn"
                      onClick={removeCoupon}
                      className="px-5 py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 rounded-sm text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {couponInfo?.valid && (
                  <div data-testid="sub-coupon-applied" className="mt-3 text-xs text-[#00E676] font-mono flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {couponInfo.code} accepted — {couponInfo.is_permanent ? "Permanent access" : `${couponInfo.duration_days}-day access`} · {couponInfo.label}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Free-coupon instant redeem (works for permanent + duration coupons) */}
        {hasCoupon && status !== "pending" && status !== "active" && (
          <div className="border border-[#00E676]/40 bg-[#00E676]/5 rounded-md p-5 mb-6">
            <div className="flex items-start gap-3 mb-4">
              <Sparkles className="w-6 h-6 text-[#00E676] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-lg font-semibold text-white">
                  {couponInfo.is_permanent ? "You qualify for permanent free access" : `You qualify for a ${couponInfo.duration_days}-day free trial`}
                </div>
                <p className="text-xs text-zinc-300 mt-1">
                  Coupon <span className="font-mono text-[#00E676]">{couponInfo.code}</span> {couponInfo.is_permanent ? "grants permanent" : `unlocks ${couponInfo.duration_days} days of`} access — no payment required. Click below to activate immediately.
                </p>
              </div>
            </div>
            <button
              data-testid="sub-redeem-free-btn"
              onClick={redeemFree}
              disabled={submitting}
              className="w-full md:w-auto px-6 py-3 bg-[#00E676] hover:bg-[#00FF88] text-black font-bold rounded-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {couponInfo.is_permanent ? "Activate Permanent Access" : `Activate ${couponInfo.duration_days}-Day Trial`}
            </button>
          </div>
        )}

        {/* Upload screenshot form */}
        {status !== "pending" && !hasCoupon && (
          <div className="border border-zinc-900 bg-[#0F0F0F] rounded-md p-5">
            <h2 className="font-display text-lg font-semibold mb-3">Submit Payment Proof</h2>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 font-mono mb-1.5">TX Screenshot (required)</label>
                <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile}
                  data-testid="sub-file-input"
                  className="block w-full text-xs text-zinc-300 file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:text-xs file:font-mono file:bg-[#00E676]/15 file:text-[#00E676] hover:file:bg-[#00E676]/25" />
                {previewB64 && (
                  <img src={previewB64} alt="preview" className="mt-3 w-full max-h-56 object-contain bg-black border border-zinc-800 rounded-sm" />
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-zinc-500 font-mono mb-1.5">TX Hash (optional)</label>
                  <input data-testid="sub-tx-hash" value={txHash} onChange={e => setTxHash(e.target.value)}
                    placeholder="0x... or Tron TX id"
                    className="w-full bg-black border border-zinc-800 text-white px-3 py-2.5 rounded-sm font-mono text-xs focus:border-[#00E676] outline-none" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-zinc-500 font-mono mb-1.5">Note (optional)</label>
                  <textarea data-testid="sub-note" value={note} onChange={e => setNote(e.target.value)}
                    rows={3} placeholder="Anything we should know?"
                    className="w-full bg-black border border-zinc-800 text-white px-3 py-2 rounded-sm text-xs focus:border-[#00E676] outline-none resize-none" />
                </div>
              </div>
            </div>
            <button data-testid="sub-submit-btn" onClick={submit} disabled={submitting || !previewB64}
              className="w-full md:w-auto px-6 py-3 bg-[#00E676] hover:bg-[#00FF88] text-black font-semibold rounded-sm flex items-center justify-center gap-2 disabled:opacity-50">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Submit for Verification
            </button>
            <p className="text-[10px] text-zinc-500 mt-3 font-mono flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Verification typically within 2 hours during business hours (IST).</p>
          </div>
        )}
      </div>
    </div>
  );
}
